import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { DeviceMobile, ShareNetwork, PlusSquare, CheckCircle, X, DotsThreeVertical } from "@phosphor-icons/react";
import { getInstallPrompt, onInstallPromptChange, isStandalone, isIOS, isMobileLike } from "../lib/installPrompt";

const dismissKey = (slug) => `fixli_door_install_dismissed:${slug}`;
const COOLDOWN_MS = 7 * 24 * 3600 * 1000;

function recentlyDismissed(slug) {
  try {
    const ts = parseInt(localStorage.getItem(dismissKey(slug)) || "0", 10);
    return ts && Date.now() - ts < COOLDOWN_MS;
  } catch { return false; }
}

/** "Add to home screen" card shown on a tenant's front door (fixlr.app/<slug>). */
export function DoorInstallPrompt({ portal }) {
  const slug = portal?.slug || "";
  const accent = portal?.primary_color || undefined;
  const [prompt, setPrompt] = useState(getInstallPrompt());
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(() => recentlyDismissed(slug));
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => onInstallPromptChange(() => {
    setPrompt(getInstallPrompt());
    if (!getInstallPrompt() && !isStandalone()) return;
  }), []);
  useEffect(() => {
    const done = () => setInstalled(true);
    window.addEventListener("appinstalled", done);
    return () => window.removeEventListener("appinstalled", done);
  }, []);

  if (!slug || dismissed || isStandalone()) return null;
  const ios = isIOS();
  const canNative = !!prompt;
  if (!canNative && !ios && !isMobileLike()) return null;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey(slug), String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const install = async () => {
    if (!prompt) return;
    setBusy(true);
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      else dismiss();
    } finally {
      setBusy(false);
    }
  };

  if (installed) {
    return (
      <div className="mt-6 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300" data-testid="door-install-done">
        <CheckCircle size={16} weight="fill" /> Added to your home screen — open {portal.company_name} from there next time.
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-xl border border-border bg-card/80 p-3" data-testid="door-install-prompt">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white" style={{ backgroundColor: accent || "#191A12" }}>
          <DeviceMobile size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">Add {portal.company_name} to your home screen</p>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            One-tap sign-in with your own app icon — no more scanning or typing the address.
          </p>
          {ios && !canNative && showIosSteps && (
            <ol className="mt-2 space-y-1 text-[11px] text-muted-foreground" data-testid="door-install-ios-steps">
              <li className="flex items-center gap-1.5"><span className="font-semibold text-foreground">1.</span> Tap <ShareNetwork size={13} className="text-blue-600" /> <span className="font-semibold text-foreground">Share</span> in Safari's toolbar</li>
              <li className="flex items-center gap-1.5"><span className="font-semibold text-foreground">2.</span> Choose <PlusSquare size={13} /> <span className="font-semibold text-foreground">Add to Home Screen</span></li>
              <li className="flex items-center gap-1.5"><span className="font-semibold text-foreground">3.</span> Tap <span className="font-semibold text-foreground">Add</span> — done</li>
            </ol>
          )}
          {!ios && !canNative && showIosSteps && (
            <ol className="mt-2 space-y-1 text-[11px] text-muted-foreground" data-testid="door-install-android-steps">
              <li className="flex items-center gap-1.5"><span className="font-semibold text-foreground">1.</span> Tap <DotsThreeVertical size={13} weight="bold" /> in your browser's toolbar</li>
              <li className="flex items-center gap-1.5"><span className="font-semibold text-foreground">2.</span> Choose <span className="font-semibold text-foreground">Add to Home screen</span> / <span className="font-semibold text-foreground">Install app</span></li>
            </ol>
          )}
        </div>
        <button type="button" onClick={dismiss} className="-mr-1 -mt-1 rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Not now" data-testid="door-install-dismiss">
          <X size={14} />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        {canNative ? (
          <Button size="sm" className="h-8 flex-1 text-xs" style={accent ? { backgroundColor: accent } : undefined} onClick={install} disabled={busy} data-testid="door-install-btn">
            {busy ? "Opening…" : "Add to home screen"}
          </Button>
        ) : (
          <Button size="sm" className="h-8 flex-1 text-xs" style={accent ? { backgroundColor: accent } : undefined} onClick={() => setShowIosSteps((v) => !v)} data-testid="door-install-howto-btn">
            {showIosSteps ? "Hide steps" : "Show me how"}
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={dismiss} data-testid="door-install-later">Not now</Button>
      </div>
    </div>
  );
}
